"use client";

import { ImgHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { FALLBACK_LOGO_IMAGE } from "@/constant";
import { IMAGE_POSTFIX_MOBILECARD, IMAGE_POSTFIX_WEBCARD, IMAGE_POSTFIX_WEBFULL } from "commonModule/globalConstant";
const LazyLoadedImage = ({
  src,
  alt,
  isLazy = false,
  readyToLoad = true,
  readyToLoadHighRes = false,
  postfix = IMAGE_POSTFIX_WEBCARD,
  highResPostfix = IMAGE_POSTFIX_WEBFULL,
  fallbackSrc = FALLBACK_LOGO_IMAGE,
  ...props
}: {
  src: string;
  alt: string;
  isLazy?: boolean;
  readyToLoad?: boolean;
  readyToLoadHighRes?: boolean;
  postfix?: typeof IMAGE_POSTFIX_WEBCARD | typeof IMAGE_POSTFIX_WEBFULL | typeof IMAGE_POSTFIX_MOBILECARD | null;
  highResPostfix?: typeof IMAGE_POSTFIX_WEBCARD | typeof IMAGE_POSTFIX_WEBFULL | typeof IMAGE_POSTFIX_MOBILECARD | "original" | null;
  fallbackSrc?: string;
} & ImgHTMLAttributes<HTMLImageElement>) => {
  const initialImageSrc = postfix ? `${src}_${postfix}` : src;
  const [imageSrc, setImageSrc] = useState(initialImageSrc);
  const isHighResLoaded = useRef(false);
  const isInView = useRef(!isLazy);
  const imgRef = useRef(null);
  const isLoaded = useRef(false);
  const hasError = useRef(false);
  const loadHighRes = useCallback(() => {
    if (isHighResLoaded.current || hasError.current) return;
    if (!highResPostfix) {
      isHighResLoaded.current = true;
      return;
    }
    const highResSrc = highResPostfix === "original" ? src : `${src}_${highResPostfix}`;
    const img = new Image();
    img.src = highResSrc;
    img.onload = () => {
      // Image Availabile, use it
      setImageSrc(highResSrc);
      isHighResLoaded.current = true;
    };
    img.onerror = () => {
      if (fallbackSrc) {
        setImageSrc(fallbackSrc);
      }
      // Not able to load high-res image, use fallback
      hasError.current = true;
      isHighResLoaded.current = true;
    };
  }, [fallbackSrc, hasError, isHighResLoaded, highResPostfix, src]);
  const handleError = () => {
    if (!readyToLoad) return;
    if (readyToLoadHighRes && postfix && !isHighResLoaded.current) {
      const highResSrc = src;
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setImageSrc(highResSrc);
        isLoaded.current = true;
      };
      img.onerror = () => {
        isLoaded.current = true;
        hasError.current = true;
        if (fallbackSrc) {
          setImageSrc(fallbackSrc);
        }
      };
      return;
    }
    if (postfix) {
      if (`${src}_${postfix}` === imageSrc) {
        setImageSrc(src);
        return;
      }
    }
    if (fallbackSrc && !hasError.current) {
      setImageSrc(fallbackSrc);
      hasError.current = true;
    }
  };
  useEffect(() => {
    if (!readyToLoadHighRes) return;

    // Determine if it's a mobile device
    const isMobileDevice = window.innerWidth < 768;
    if (isMobileDevice) {
      // If mobile device, load high-res only on hover
      return;
    }

    // Detect slow network
    let isSlowNetwork = false;
    if ("connection" in navigator) {
      const conn = navigator.connection;
      if (conn &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/dot-notation
      ["3g", "2g", "slow-2g"].includes(conn["effectiveType"] || "")) {
        isSlowNetwork = true;
      }
    }
    if (isSlowNetwork) {
      // If slow network, load high-res only on hover
      return;
    }
    if (!imgRef.current) {
      return;
    }

    // Try loading high-res when the image is in view
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return;
        }
        if ("requestIdleCallback" in window) {
          requestIdleCallback(loadHighRes, {
            timeout: 2000
          });
        } else {
          setTimeout(loadHighRes, 3000);
        }
        observer.disconnect();
      });
    }, {
      threshold: 0.1
    });
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [loadHighRes, readyToLoadHighRes]);
  useEffect(() => {
    if (isLoaded.current) return;
    if (!imgRef.current) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return;
        }
        isInView.current = true;
        observer.disconnect();
      });
    }, {
      threshold: 0.1
    });
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);
  const resolvedSrc = isLoaded.current || readyToLoad && isInView.current ? imageSrc || src : undefined;
  return <img ref={imgRef} {...resolvedSrc ? {
    src: resolvedSrc
  } : {}} alt={alt} onError={handleError} onLoad={() => {
    isLoaded.current = true;
  }} style={{
    backgroundImage: `url(${initialImageSrc})`,
    backgroundSize: "cover",
    backgroundPosition: "center"
  }} {...props} data-sentry-component="LazyLoadedImage" data-sentry-source-file="LazyLoadedImage.tsx" />;
};
export default LazyLoadedImage;